import type { SagaIterator } from '@redux-saga/types';
import { batchActions } from 'redux-batched-actions';
import { AnyAction } from 'redux-saga';
import { call, put, select, delay } from 'redux-saga/effects';

import { setCellHeight } from 'containers/ArtboardPreview/actions';
import { ActionTypes } from 'containers/ArtboardPreview/constants';
import { Action } from 'containers/ArtboardPreview/models';
import { cellsHeight as cellsHeightSelector } from 'containers/ArtboardPreview/selectors';
import { activeArtboardId as activeArtboardIdSelector } from 'containers/Artboards/selectors';
import { handleSagaError } from 'services/handleError';

// Global storage for batching cell height updates
const cellHeightUpdates: Record<string, number> = {};

// Batching mechanism to prevent multiple rapid executions
let batchTimeoutHandle: NodeJS.Timeout | null = null;
const DELAY = 100; // milliseconds

export function* updateCellsHeight(action: Action.IUpdateCellsHeight): SagaIterator {
  try {
    const { relationId, newHeight } = action.payload;
    const activeArtboardId: ReturnTypeSaga<typeof activeArtboardIdSelector> = yield select(activeArtboardIdSelector);
    const key = `${activeArtboardId}:${relationId}`;

    // Store the update in the batch
    cellHeightUpdates[key] = newHeight;

    // If there's already a batch scheduled, it's already added to the queue - return
    if (batchTimeoutHandle) {
      return;
    }

    // Schedule batch processing after a small delay
    batchTimeoutHandle = setTimeout(() => {
      batchTimeoutHandle = null;
      // The actual processing will be done by the first call that hasn't been deferred
    }, DELAY);

    // Add a small delay to allow other rapid calls to be batched
    yield delay(DELAY);

    // Process all batched updates
    const cellsHeight: ReturnTypeSaga<typeof cellsHeightSelector> = yield select(cellsHeightSelector);

    // Collect all actions to batch
    const actions: AnyAction[] = [];
    const keysToProcess = Object.keys(cellHeightUpdates);

    for (const batchKey of keysToProcess) {
      const batchNewHeight = cellHeightUpdates[batchKey];

      // Only update if the height has actually changed
      if (batchNewHeight !== cellsHeight.get(batchKey)) {
        actions.push(setCellHeight(batchKey, batchNewHeight));
      }
    }

    // Dispatch all actions in a single batch if there are any to process
    if (actions.length > 0) {
      yield put(batchActions(actions, ActionTypes.UPDATE_CELLS_HEIGHT));
    }

    // Clear processed updates
    keysToProcess.forEach((batchedKey) => {
      delete cellHeightUpdates[batchedKey];
    });

  } catch (error) {
    yield call(handleSagaError, error, 'ArtboardPreview.updateCellsHeight', 'UpdateCellsHeight');
  }
}
